import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Autocomplete, CircularProgress, TextField } from '@mui/material'
import { useSmartTranslation } from '../../hooks/useSmartTranslation'
import { searchUsers } from './labelPrintingApi'

export const USER_SEARCH_DELAY_MS = 300

const usernameOf = (candidate) => (typeof candidate === 'string' ? candidate : candidate?.username || '')

// Historia 226, E18 (S226): user picker with search in Sun. Typing asks `GET user` with `genericSearch=<text>` after
// 300 ms without keystrokes (no text: the first 50 at once); only the latest answer is kept. Reused by "Assign to users"
// (E13) and the user filter of the history (E15). `value` is a username or '' and `onChange` receives a username or ''.
const UserAutocomplete = ({ value = '', onChange, label, multiple = false }) => {
  const { t_ } = useSmartTranslation()
  const [options, setOptions] = useState([])
  // Multiple mode (E21): `value` is a list of usernames and `onChange` receives the list.
  const chosen = multiple ? (Array.isArray(value) ? value : []) : value
  const [inputValue, setInputValue] = useState(multiple ? '' : value)
  const [loading, setLoading] = useState(false)
  const latest = useRef(0)

  useEffect(() => {
    latest.current += 1
    const ticket = latest.current
    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        const response = await searchUsers(inputValue)
        const list = Array.isArray(response) ? response : (response?.content || [])
        if (ticket === latest.current) setOptions(list.map(usernameOf).filter(Boolean))
      } catch (error) {
        if (ticket === latest.current) setOptions([])
      } finally {
        if (ticket === latest.current) setLoading(false)
      }
    }, inputValue === '' ? 0 : USER_SEARCH_DELAY_MS)
    return () => clearTimeout(timer)
  }, [inputValue])

  const handleChange = (event, selected) => onChange(multiple ? selected : selected || '')
  // Stable: Autocomplete resets its text whenever this handler changes while the field has no focus.
  const handleInputChange = useCallback((event, text) => setInputValue(text), [])
  const shown = multiple
    ? [...chosen, ...options.filter((username) => !chosen.includes(username))]
    : (value && !options.includes(value) ? [value, ...options] : options)

  return (
    <Autocomplete
      options={shown}
      multiple={multiple}
      filterSelectedOptions={multiple}
      value={multiple ? chosen : value || null}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      filterOptions={(all) => all}
      loading={loading}
      loadingText={t_('Loading…')}
      noOptionsText={t_('No users found')}
      size='small'
      fullWidth
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (<>{loading ? <CircularProgress color='inherit' size={16} /> : null}{params.InputProps.endAdornment}</>)
          }}
        />
      )}
    />
  )
}

export default UserAutocomplete
